import * as THREE from 'three';
import { alongWorldXY, camPlaneToWorldXY, worldToCam } from './camera';
import { gradeSeparation, gradeVec } from './grade';
import { capPush, engageSlack, rimPad, softBand, titleHoldPad, titlePad } from './pads';

/** Same-depth "who is in front" must not flip on a 1° orbit step. */
const VIEW_Z_STICK = 0.55;

/**
 * Smallest XY nudge so `node` clears `hub` on screen.
 * By default only when `hub` is in front. `always` also separates when the
 * hub sits behind — used so every child yields to every parent.
 */
export function sameDepthYields(entryViewZ, otherViewZ, sticky) {
  if (sticky) return otherViewZ < entryViewZ + VIEW_Z_STICK;
  return otherViewZ < entryViewZ - 0.04;
}

export function minXYClear(camera, hub, node, hubR, nodeR, always = false, held = false) {
  const h = worldToCam(hub, camera);
  const n = worldToCam(node, camera);
  if (h.z < 0.25 || n.z < 0.25) return { x: 0, y: 0 };
  if (!always && n.z <= h.z + 0.04) return { x: 0, y: 0 };

  const scale = h.z / Math.max(n.z, 0.3);
  let dx = n.x * scale - h.x;
  let dy = n.y * scale - h.y;
  const d = Math.hypot(dx, dy);
  const need = hubR + nodeR * scale + rimPad(h.z) - engageSlack(h.z, 0.5);
  const keep = need + titleHoldPad(h.z) * 0.45;
  if (d >= (held ? keep : need)) return { x: 0, y: 0 };

  const extraAtNode = gradeSeparation(d, need, softBand(h.z)) / scale;
  if (extraAtNode <= 1e-6) return { x: 0, y: 0 };
  let push;
  if (d < 1e-4) {
    push = alongWorldXY(node.x - hub.x, node.y - hub.y, extraAtNode);
  } else {
    push = camPlaneToWorldXY((extraAtNode * dx) / d, (extraAtNode * dy) / d);
  }
  return capPush(push);
}

/** Shortest camera-plane push so a disk leaves an AABB (title). */
function circleAabbPush(cx, cy, r, left, right, bottom, top) {
  const inside = cx >= left && cx <= right && cy >= bottom && cy <= top;
  if (inside) {
    const toL = cx - left;
    const toR = right - cx;
    const toB = cy - bottom;
    const toT = top - cy;
    const nearest = Math.min(toL, toR, toB, toT);
    if (nearest === toL) return { x: -(r + toL), y: 0 };
    if (nearest === toR) return { x: r + toR, y: 0 };
    if (nearest === toB) return { x: 0, y: -(r + toB) };
    return { x: 0, y: r + toT };
  }
  const closestX = THREE.MathUtils.clamp(cx, left, right);
  const closestY = THREE.MathUtils.clamp(cy, bottom, top);
  const dx = cx - closestX;
  const dy = cy - closestY;
  const d = Math.hypot(dx, dy);
  if (d >= r) return { x: 0, y: 0 };
  const extra = r - Math.max(d, 1e-4);
  return { x: (dx / d) * extra, y: (dy / d) * extra };
}

/**
 * CSS2D titles always paint over the canvas. Nudge a node in XY so its
 * screen disk leaves the title box — same camera-plane math as nest reveal.
 */
export function minXYClearTitle(camera, title, node, nodeR) {
  const n = worldToCam(node, camera);
  if (title.tz < 0.25 || n.z < 0.25) return { x: 0, y: 0 };
  const scale = title.tz / Math.max(n.z, 0.3);
  const slack = -engageSlack(title.tz);
  const pushCam = circleAabbPush(
    n.x * scale,
    n.y * scale,
    nodeR * scale + titlePad(title.tz) + slack,
    title.left,
    title.right,
    title.bottom,
    title.top,
  );
  if (pushCam.x === 0 && pushCam.y === 0) return { x: 0, y: 0 };
  return capPush(gradeVec(camPlaneToWorldXY(pushCam.x / scale, pushCam.y / scale), softBand(title.tz)));
}

function aabbAabbPush(al, ar, ab, at, bl, br, bb, bt) {
  const overlapX = Math.min(ar, br) - Math.max(al, bl);
  const overlapY = Math.min(at, bt) - Math.max(ab, bb);
  if (overlapX <= 0 || overlapY <= 0) return { x: 0, y: 0 };
  if (overlapX < overlapY) {
    const aMid = (al + ar) * 0.5;
    const bMid = (bl + br) * 0.5;
    return { x: aMid >= bMid ? overlapX : -overlapX, y: 0 };
  }
  const aMid = (ab + at) * 0.5;
  const bMid = (bb + bt) * 0.5;
  return { x: 0, y: aMid >= bMid ? overlapY : -overlapY };
}

function aabbCirclePush(al, ar, ab, at, cx, cy, r) {
  if (cx >= al && cx <= ar && cy >= ab && cy <= at) {
    const toL = cx - al;
    const toR = ar - cx;
    const toB = cy - ab;
    const toT = at - cy;
    const nearest = Math.min(toL, toR, toB, toT);
    if (nearest === toL) return { x: toL + r, y: 0 };
    if (nearest === toR) return { x: -(toR + r), y: 0 };
    if (nearest === toB) return { x: 0, y: toB + r };
    return { x: 0, y: -(toT + r) };
  }
  const closestX = THREE.MathUtils.clamp(cx, al, ar);
  const closestY = THREE.MathUtils.clamp(cy, ab, at);
  const dx = closestX - cx;
  const dy = closestY - cy;
  const d = Math.hypot(dx, dy);
  if (d >= r) return { x: 0, y: 0 };
  const extra = r - Math.max(d, 1e-4);
  return { x: (dx / d) * extra, y: (dy / d) * extra };
}

/** Push a hanging name off a shallower disk. */
export function minXYClearHangDisk(camera, hub, hubR, hang) {
  const h = worldToCam(hub, camera);
  if (h.z < 0.25 || hang.tz < 0.25) return { x: 0, y: 0 };
  const scale = h.z / Math.max(hang.tz, 0.3);
  const slack = -engageSlack(h.z, 0.35);
  const pushCam = aabbCirclePush(
    hang.left * scale,
    hang.right * scale,
    hang.bottom * scale,
    hang.top * scale,
    h.x,
    h.y,
    hubR + rimPad(h.z) + slack,
  );
  if (pushCam.x === 0 && pushCam.y === 0) return { x: 0, y: 0 };
  return capPush(gradeVec(camPlaneToWorldXY(pushCam.x / scale, pushCam.y / scale), softBand(h.z)));
}

/** Push a hanging name off a shallower name, in the same camera-plane frame. */
export function minXYClearHang(camera, name, hang) {
  if (name.tz < 0.25 || hang.tz < 0.25) return { x: 0, y: 0 };
  const scale = name.tz / Math.max(hang.tz, 0.3);
  const slack = -engageSlack(name.tz, 0.35);
  const pushCam = aabbAabbPush(
    hang.left * scale - slack,
    hang.right * scale + slack,
    hang.bottom * scale - slack,
    hang.top * scale + slack,
    name.left,
    name.right,
    name.bottom,
    name.top,
  );
  if (pushCam.x === 0 && pushCam.y === 0) return { x: 0, y: 0 };
  return capPush(gradeVec(camPlaneToWorldXY(pushCam.x / scale, pushCam.y / scale), softBand(name.tz)));
}

